import { useEffect, useMemo, useState, type ReactNode } from "react"
import { router } from "@inertiajs/react"
import { AlertTriangle, Info, Tags, TrendingUp } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { Sheet, SheetHeader, SheetBody } from "@/components/ui/sheet"
import { Select } from "@/components/ui/select"
import { FeedbackDetailDrawer } from "@/components/domain/feedback-detail-drawer"
import { Sparkline } from "@/components/ui/sparkline"
import { fmtCompactRelative, fmtNumber } from "@/lib/format"
import { cn } from "@/lib/cn"
import type {
  FeedbackAnalysisPanel,
  FeedbackDrawerData,
  FeedbackMomentumLens,
  FeedbackRow,
  UserFeedbackProps,
} from "@/types/user_feedback"

// Selection model: clicking a row sets local `selectedId` and we lazy-fetch
// /user_feedback/:id (Accept: application/json) for the drawer payload.

const TONE_BY_CATEGORY: Record<string, BadgeTone> = {
  ai_agent_complaint: "danger",
  bug:                "red",
  recommendation:     "gold",
  unresolved:         "warning",
  general:            "neutral",
  bad_behavior:       "error",
}

function toneFor(category: string): BadgeTone {
  return TONE_BY_CATEGORY[category] ?? "neutral"
}

type RollupSortKey = "lastReported" | "reportsDesc" | "titleAsc"

const ROLLUP_SORT_OPTIONS: { value: RollupSortKey; label: string }[] = [
  { value: "lastReported", label: "Recently mentioned" },
  { value: "reportsDesc",  label: "Most reports" },
  { value: "titleAsc",     label: "Title A->Z" },
]

const EXPLAINER = `Feedback items are surfaced from real agent conversations. \
Bugs are reproducible product defects, recommendations are feature or product \
ideas, and AI complaints are negative AI-rating follow-ups captured by the \
platform completion flow. The category list comes from rollups already present \
in the database.`

export default function UserFeedbackIndex({
  rows,
  summary,
  buckets,
  analysis,
  trend,
  filter,
}: UserFeedbackProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerData, setDrawerData] = useState<FeedbackDrawerData | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [explainerOpen, setExplainerOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(filter.q ?? "")
  const [rollupSort, setRollupSort] = useState<RollupSortKey>("lastReported")

  // Fetch detail when a row is selected. We deliberately don't prefetch — most
  // operators only open one or two items per visit.
  useEffect(() => {
    if (selectedId == null) {
      setDrawerData(null)
      return
    }
    let cancelled = false
    setDrawerLoading(true)
    fetch(`/user_feedback/${selectedId}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: FeedbackDrawerData = await res.json()
        if (!cancelled) setDrawerData(data)
      })
      .catch(() => {
        if (!cancelled) setDrawerData(null)
      })
      .finally(() => {
        if (!cancelled) setDrawerLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId])

  function buildUrl(patch: Partial<{ category: string | null; q: string | null; from: string | null; to: string | null }>) {
    const merged = { ...filter, ...patch }
    const params = new URLSearchParams()
    if (merged.category) params.set("category", merged.category)
    if (merged.q) params.set("q", merged.q)
    if (merged.from) params.set("from", merged.from)
    if (merged.to) params.set("to", merged.to)
    const qs = params.toString()
    return qs ? `/user_feedback?${qs}` : "/user_feedback"
  }

  function setCategory(key: string | null) {
    router.visit(buildUrl({ category: key }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function applySearch() {
    const next = searchValue.trim() || null
    router.visit(buildUrl({ q: next }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function setDate(field: "from" | "to", value: string) {
    router.visit(buildUrl({ [field]: value || null }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const activeCategory = filter.category ?? "all"
  const activeBucket = buckets.find((bucket) => bucket.key === activeCategory)
  const sortedRows = useMemo(() => sortRollups(rows, rollupSort), [rows, rollupSort])

  return (
    <AppShell>
      <PageHeader title="Feedback" />

      <div className="-mt-4 flex items-center gap-2 text-sm text-app-muted">
        <span>
          Rollups from real conversations, grouped for analysis before digging into reports.
        </span>
        <button
          type="button"
          onClick={() => setExplainerOpen(true)}
          aria-label="What is Feedback?"
          className="inline-flex items-center justify-center rounded-full p-0.5 text-app-muted hover:text-app-text"
        >
          <Info size={14} strokeWidth={2} />
        </button>
      </div>

      <Dialog open={explainerOpen} onClose={() => setExplainerOpen(false)} size="md">
        <DialogHeader title="About feedback categories" />
        <DialogBody>
          <p className="text-sm leading-relaxed text-app-text">{EXPLAINER}</p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setExplainerOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </Dialog>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <AnalysisPanel
          panel={analysis.aiAgentComplaints}
          featured
          onSelectRollup={setSelectedId}
        />
        <AnalysisPanel
          panel={analysis.selected}
          subtitle={activeBucket?.label ?? "All"}
          controls={
            <CategoryFilter
              buckets={buckets}
              activeCategory={activeCategory}
              onSelect={setCategory}
            />
          }
          onSelectRollup={setSelectedId}
        />
      </section>

      <section>
        <MomentumPanel lenses={trend.lenses} onSelectRollup={setSelectedId} />
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-app-text">Explore rollups</div>
            <div className="text-xs text-app-muted">
              {sortedRows.length} shown · {fmtNumber(summary.reports)} linked reports
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-3 lg:w-auto">
            <Input
              value={searchValue}
              placeholder="Search title, description, customer..."
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch()
              }}
              className="w-full min-w-0 flex-1 sm:w-auto sm:min-w-[260px]"
            />
            <div className="flex items-center gap-2 text-xs text-app-muted">
              <input
                type="date"
                value={filter.from ?? ""}
                onChange={(e) => setDate("from", e.target.value)}
                className="rounded-control border border-border bg-white/5 px-2 py-1.5 text-app-text outline-none focus:border-brand-red/50"
              />
              <span>-</span>
              <input
                type="date"
                value={filter.to ?? ""}
                onChange={(e) => setDate("to", e.target.value)}
                className="rounded-control border border-border bg-white/5 px-2 py-1.5 text-app-text outline-none focus:border-brand-red/50"
              />
            </div>
            <div className="min-w-[210px]">
              <Select<RollupSortKey>
                value={ROLLUP_SORT_OPTIONS.find((o) => o.value === rollupSort) ?? ROLLUP_SORT_OPTIONS[0]}
                options={ROLLUP_SORT_OPTIONS}
                onChange={(o) => o && setRollupSort(o.value)}
                isSearchable={false}
              />
            </div>
          </div>
        </div>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        {sortedRows.length === 0 ? (
          <Card variant="inset" className="p-6 text-sm text-app-muted">
            No feedback rollups in this view yet.
          </Card>
        ) : (
          <>
            {/* Desktop: full table */}
            <Card className="hidden overflow-hidden p-0 lg:block">
              <div className="flex items-center justify-between border-b border-border bg-app-panel2/30 px-4 py-3">
                <div className="text-sm font-medium text-app-text">Feedback rollups</div>
                <div className="text-xs text-app-muted">
                  {sortedRows.length} shown · {fmtNumber(summary.reports)} linked reports
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-white/[0.03] text-xs uppercase tracking-wide text-app-subtle">
                    <th className="px-4 py-3 text-left">Rollup</th>
                    <th className="px-4 py-3 text-left">Analysis</th>
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-left">Source</th>
                    <th className="px-4 py-3 text-right">Reports</th>
                    <th className="px-4 py-3 text-right">Users</th>
                    <th className="px-4 py-3 text-right">Last seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedRows.map((r) => {
                    const selected = selectedId === r.id
                    return (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedId(r.id)}
                        className={cn(
                          "cursor-pointer text-app-text transition",
                          selected
                            ? "bg-brand-redSoft/30"
                            : "hover:bg-white/5",
                        )}
                      >
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-app-text">{r.title}</div>
                          {r.feedbackKey && (
                            <div className="mt-1 font-mono text-[11px] text-app-muted">
                              {r.feedbackKey}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-app-text">{r.analysisLabel}</div>
                          <div className="mt-1 max-w-[280px] text-xs leading-relaxed text-app-muted">
                            {r.analysisDescription}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <Badge tone={toneFor(r.category)}>{r.categoryLabel}</Badge>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-app-text">{r.agentName ?? "—"}</div>
                          {r.skillName && (
                            <div className="text-xs text-app-muted">{r.skillName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-sm text-app-text">
                          {r.reportCount}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-sm text-app-text">
                          {r.uniqueAppuserCount}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-xs text-app-muted">
                          {fmtCompactRelative(r.lastReportedAt ?? r.createdAt)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </Card>

            {/* Mobile/tablet: card list */}
            <div className="space-y-3 lg:hidden">
              {sortedRows.map((r) => {
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className="block w-full rounded-card border border-border bg-app-panel p-4 text-left transition hover:border-brand-red/40 hover:bg-white/[0.04]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-app-text">{r.title}</div>
                        <div className="mt-1 text-xs text-app-muted">
                          {r.agentName ?? "—"}
                          {r.feedbackKey && <span className="ml-1">· {r.feedbackKey}</span>}
                        </div>
                        <div className="mt-2 text-xs font-medium text-app-text">
                          {r.analysisLabel}
                        </div>
                      </div>
                      <Badge tone={toneFor(r.category)}>{r.categoryLabel}</Badge>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <span className="text-xs text-app-muted">
                        {r.reportCount} report{r.reportCount === 1 ? "" : "s"} · {r.uniqueAppuserCount} user{r.uniqueAppuserCount === 1 ? "" : "s"}
                      </span>
                      <span className="shrink-0 text-xs text-app-muted">{fmtCompactRelative(r.lastReportedAt ?? r.createdAt)}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        )}

        {/* Desktop: side-rail card. Hidden on mobile; sheet handles it instead. */}
        <div className="hidden lg:block">
          {selectedId == null ? (
            <FeedbackDetailDrawer data={null} onClose={() => setSelectedId(null)} />
          ) : drawerLoading && !drawerData ? (
            <Card className="p-6 text-sm text-app-muted">Loading…</Card>
          ) : (
            <FeedbackDetailDrawer
              data={drawerData}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      </section>
      </section>

      {/* Mobile/tablet: full-height side sheet for the selected detail.
          On lg+ the inline side-rail card above handles it, so the sheet
          itself is `display:none` on lg+ to avoid rendering twice. */}
      <Sheet
        open={selectedId != null}
        onClose={() => setSelectedId(null)}
        side="right"
        className="lg:!hidden"
      >
        <SheetHeader title="Feedback detail" onClose={() => setSelectedId(null)} />
        <SheetBody>
          {drawerLoading && !drawerData ? (
            <div className="text-sm text-app-muted">Loading…</div>
          ) : (
            <FeedbackDetailDrawer
              data={drawerData}
              onClose={() => setSelectedId(null)}
            />
          )}
        </SheetBody>
      </Sheet>
    </AppShell>
  )
}

function sortRollups(rows: FeedbackRow[], sort: RollupSortKey): FeedbackRow[] {
  return [...rows].sort((a, b) => {
    if (sort === "titleAsc") return a.title.localeCompare(b.title)
    if (sort === "reportsDesc") return b.reportCount - a.reportCount
    return Date.parse(b.lastReportedAt ?? b.createdAt) - Date.parse(a.lastReportedAt ?? a.createdAt)
  })
}

function AnalysisPanel({
  panel,
  subtitle,
  controls,
  featured = false,
  onSelectRollup,
}: {
  panel: FeedbackAnalysisPanel
  subtitle?: string
  controls?: ReactNode
  featured?: boolean
  onSelectRollup: (id: number) => void
}) {
  const hasGroups = panel.groups.length > 0
  const Icon = featured ? AlertTriangle : Tags

  return (
    <Card className={cn("space-y-4 p-5", featured && "border-tone-danger/30 bg-tone-dangerSoft/10")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon
            size={16}
            strokeWidth={2}
            className={featured ? "text-tone-danger" : "text-brand-red"}
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-app-text">{panel.title}</div>
            {subtitle && <div className="text-xs text-app-muted">{subtitle}</div>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-right text-xs text-app-muted">
          <div>
            <div className="text-sm font-semibold tabular-nums text-app-text">{fmtNumber(panel.rollupCount)}</div>
            <div>rollups</div>
          </div>
          <div>
            <div className="text-sm font-semibold tabular-nums text-app-text">{fmtNumber(panel.reportCount)}</div>
            <div>reports</div>
          </div>
          <div>
            <div className="text-sm font-semibold tabular-nums text-app-text">{fmtNumber(panel.uniqueAppuserCount)}</div>
            <div>users</div>
          </div>
        </div>
      </div>

      {controls}

      {!hasGroups ? (
        <div className="rounded-control border border-border bg-white/[0.03] px-3 py-4 text-sm text-app-muted">
          No rollups in this view yet.
        </div>
      ) : (
        <div className="space-y-3">
          {panel.groups.map((group) => (
            <div
              key={group.key}
              className="rounded-control border border-border bg-white/[0.03] p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-app-text">{group.label}</div>
                  <div className="mt-1 text-xs leading-relaxed text-app-muted">
                    {group.description}
                  </div>
                </div>
                <div className="shrink-0 text-right text-xs text-app-muted">
                  <div className="text-sm font-semibold tabular-nums text-app-text">
                    {fmtNumber(group.reportCount)}
                  </div>
                  <div>reports</div>
                </div>
              </div>

              <div className="mt-3 grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
                <div className="h-10">
                  <Sparkline values={group.values} height={40} variant="area" />
                </div>
                <div className="text-xs text-app-muted sm:text-right">
                  {fmtNumber(group.rollupCount)} rollup{group.rollupCount === 1 ? "" : "s"}
                  {group.lastSeenAt && (
                    <span className="block">last {fmtCompactRelative(group.lastSeenAt)}</span>
                  )}
                </div>
              </div>

              {group.topRollups.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {group.topRollups.map((rollup) => (
                    <button
                      key={rollup.id}
                      type="button"
                      onClick={() => onSelectRollup(rollup.id)}
                      className="grid w-full grid-cols-[minmax(0,1fr)_64px] items-center gap-3 rounded-control border border-border bg-app-panel/70 px-3 py-2 text-left transition hover:border-brand-red/40 hover:bg-white/[0.06]"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium text-app-text">{rollup.title}</div>
                        <div className="mt-0.5 text-[11px] text-app-muted">{rollup.categoryLabel}</div>
                      </div>
                      <div className="text-right text-xs tabular-nums text-app-muted">
                        {fmtNumber(rollup.reportCount)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function CategoryFilter({
  buckets,
  activeCategory,
  onSelect,
}: {
  buckets: UserFeedbackProps["buckets"]
  activeCategory: string
  onSelect: (key: string | null) => void
}) {
  const filterBuckets = buckets.filter((bucket) => bucket.key !== "ai_agent_complaint")

  return (
    <div className="space-y-2 rounded-control border border-border bg-white/[0.03] p-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-app-subtle">
        <Tags size={13} strokeWidth={2} className="text-brand-red" />
        Category filter
      </div>
      <div className="flex flex-wrap gap-2">
        {filterBuckets.map((b) => {
          const active = activeCategory === b.key
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => onSelect(b.key === "all" ? null : String(b.key))}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-chip border px-3 py-1.5 text-xs font-medium transition",
                active
                  ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
                  : "border-border bg-app-panel/70 text-app-text hover:bg-white/10",
              )}
            >
              {b.label}
              <span className={cn("text-[11px]", active ? "text-brand-red" : "text-app-muted")}>
                {b.count}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function MomentumPanel({
  lenses,
  onSelectRollup,
}: {
  lenses: FeedbackMomentumLens[]
  onSelectRollup: (id: number) => void
}) {
  const [activeKey, setActiveKey] = useState(lenses[0]?.key ?? "")
  const activeLens = lenses.find((lens) => lens.key === activeKey) ?? lenses[0]

  if (!activeLens) {
    return (
      <Card className="p-5 text-sm text-app-muted">
        No recent report activity.
      </Card>
    )
  }

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} strokeWidth={2} className="text-brand-red" />
          <div>
            <div className="text-sm font-medium text-app-text">Momentum</div>
            <div className="text-xs text-app-muted">Last 7 days compared with the prior 23-day pace</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 rounded-control border border-border bg-white/[0.03] p-1">
          {lenses.map((lens) => {
            const active = lens.key === activeLens.key
            return (
              <button
                key={lens.key}
                type="button"
                onClick={() => setActiveKey(lens.key)}
                className={cn(
                  "rounded-control px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "bg-brand-redSoft text-brand-red"
                    : "text-app-muted hover:bg-white/[0.06] hover:text-app-text",
                )}
              >
                {lens.label}
              </button>
            )
          })}
        </div>
      </div>

      {activeLens.reportCount === 0 ? (
        <div className="rounded-control border border-border bg-white/[0.03] px-3 py-4 text-sm text-app-muted">
          No report activity in this date range.
        </div>
      ) : (
        <>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="rounded-control border border-border bg-white/[0.03] p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <Badge tone={toneFor(activeLens.category ?? "")}>{activeLens.label}</Badge>
                <span className="text-xs text-app-muted">30-day volume</span>
              </div>
              <div className="h-24">
                <Sparkline values={activeLens.values} height={96} variant="area" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-control border border-border bg-white/[0.03] p-3">
                <div className="text-xs text-app-muted">Last 7</div>
                <div className="mt-2 text-2xl font-semibold tabular-nums text-app-text">
                  {fmtNumber(activeLens.recentCount)}
                </div>
              </div>
              <div className="rounded-control border border-border bg-white/[0.03] p-3">
                <div className="text-xs text-app-muted">Expected</div>
                <div className="mt-2 text-2xl font-semibold tabular-nums text-app-text">
                  {fmtNumber(activeLens.expectedRecentCount)}
                </div>
              </div>
              <div className="rounded-control border border-border bg-white/[0.03] p-3">
                <div className="text-xs text-app-muted">Lift</div>
                <div className={cn(
                  "mt-2 text-2xl font-semibold tabular-nums",
                  activeLens.liftPct > 0 ? "text-tone-danger" : "text-app-text",
                )}>
                  {formatLift(activeLens.liftPct)}
                </div>
              </div>
            </div>
          </div>

          {activeLens.topRollups.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-app-subtle">
                Heating up rollups
              </div>
              <div className="space-y-1.5">
                {activeLens.topRollups.map((rollup) => (
                  <button
                    key={rollup.id}
                    type="button"
                    onClick={() => onSelectRollup(rollup.id)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_120px_76px] items-center gap-3 rounded-control border border-border bg-white/[0.03] px-3 py-2 text-left transition hover:border-brand-red/40 hover:bg-white/[0.06]"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-app-text">{rollup.title}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-app-muted">
                        <span>{rollup.categoryLabel}</span>
                        <span>{fmtNumber(rollup.recentCount)} last 7</span>
                        <span>{fmtNumber(rollup.expectedRecentCount)} expected</span>
                      </div>
                    </div>
                    <Sparkline values={rollup.values} height={28} />
                    <div className={cn(
                      "text-right text-xs font-medium tabular-nums",
                      rollup.liftPct > 0 ? "text-tone-danger" : "text-app-muted",
                    )}>
                      {formatLift(rollup.liftPct)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  )
}

function formatLift(value: number): string {
  if (value > 0) return `+${value}%`
  return `${value}%`
}
